/**
 * Two ways to hang an explanation off something, both built on the shared `Tooltip`
 * primitive (`src/rescue/components/Tooltip/Tooltip.tsx`), which supplies the portal,
 * positioning, and scroll/Escape bookkeeping:
 *
 *   - {@link InfoTip} adds its own trigger — a small circled "i" beside a label, quiet
 *     enough to live in a table header without competing with the column name. It is a
 *     button, so it is reachable by keyboard.
 *   - {@link HoverTip} adds no trigger: it wraps content that is already on screen and
 *     explains it on hover.
 *
 * Pair them when the same thing is worth saying in both places. The header gets the
 * InfoTip, which carries the keyboard and screen-reader route; the rows get HoverTips,
 * which are mouse-only by design.
 *
 * They open differently, and the difference is the point. An InfoTip is a control someone
 * aimed at, so it opens the instant it is touched. A HoverTip sits under content that a
 * pointer crosses on its way elsewhere, so it waits out a delay and fades, which is what
 * keeps a table of badges from flickering as the mouse travels over it.
 */

import React, { useState } from 'react';
import Tooltip, { TOOLTIP_FADE_MS, TOOLTIP_HOVER_DELAY_MS, useTooltipState } from '../Tooltip/Tooltip';
import NoticeIcon from '../NoticeIcon/NoticeIcon';
import styles from './InfoTip.module.scss';

/**
 * Source of the per-instance tooltip id used by `aria-describedby`. React 17 has no
 * `useId`, and the ids have to stay unique across every table on the page, so they come
 * from a module counter read once into state — assigned at mount, stable across renders.
 */
let nextTipId = 0;

function useTipId(): string {
  const [tipId] = useState(() => {
    nextTipId += 1;
    return `rp-infotip-${nextTipId}`;
  });
  return tipId;
}

interface InfoTipProps {
  /**
   * Accessible name for the trigger — the glyph itself is decorative, so without this the
   * control reads as an unlabelled button. Say what it explains, e.g. "About the Setup column".
   */
  label: string;
  /** Extra class for caller-supplied positioning (e.g. nudging the glyph in a header). */
  className?: string;
  /** The explanation itself. */
  children: React.ReactNode;
}

export default function InfoTip({ label, className, children }: InfoTipProps) {
  const tipId = useTipId();
  // Always active: unlike the import table's error tooltips there is no "nothing to say"
  // state — an InfoTip is only rendered where there is an explanation to give.
  //
  // Neither delayed nor faded, unlike the HoverTips on the badges themselves. Nobody lands
  // on this glyph by accident — it is a target the reader aimed at — so there is no stray
  // hover to guard against, and the answer to a deliberate question should simply be there.
  const { triggerRef, open, pos, handleEnter, handleLeave } = useTooltipState<HTMLButtonElement>(true);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={className ? `${styles.trigger} ${className}` : styles.trigger}
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        // A touch device has no hover to open on, so a press does it. It still closes the
        // same ways everything else does: blur, Escape, or a scroll.
        onClick={handleEnter}
      >
        <NoticeIcon variant="info" className={styles.icon} />
      </button>
      {open && (
        <Tooltip pos={pos} id={tipId}>
          {children}
        </Tooltip>
      )}
    </>
  );
}

interface HoverTipProps {
  /** The explanation, shown while the wrapped content is hovered. */
  tip: React.ReactNode;
  /** Class for the wrapper span — it replaces the element the children used to sit in. */
  className?: string;
  children: React.ReactNode;
}

/**
 * Wraps on-screen content so hovering it explains it. The wrapper is a plain `<span>` and
 * takes the caller's class, so it can stand in for a layout element the children already
 * had rather than adding a level.
 *
 * Mouse-hover only, and deliberately not focusable: this exists to annotate content that
 * repeats down a table, and a tab stop per row would make the table tab-noisy for the
 * keyboard users it is supposed to help — the same trade the import table's
 * `ErrorTooltipCell` makes. Only use it where the text is also reachable some other way,
 * such as an {@link InfoTip} on the column header.
 */
export function HoverTip({ tip, className, children }: HoverTipProps) {
  const tipId = useTipId();
  // Delayed and faded, because this trigger is content rather than a control: a pointer
  // crossing a table of badges passes over plenty of these without meaning to, and the
  // delay plus the fade keep that from reading as flashing.
  const { triggerRef, open, shown, pos, handleEnter, handleLeave } = useTooltipState<HTMLSpanElement>(true, {
    hoverDelayMs: TOOLTIP_HOVER_DELAY_MS,
    fadeMs: TOOLTIP_FADE_MS,
  });

  return (
    <span
      ref={triggerRef}
      className={className}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      aria-describedby={open ? tipId : undefined}
    >
      {children}
      {open && (
        <Tooltip pos={pos} id={tipId} visible={shown}>
          {tip}
        </Tooltip>
      )}
    </span>
  );
}
